import { supabase } from './supabase';

/**
 * Check if current user is an admin
 */
export async function checkIsAdmin(userId = null) {
  try {
    let userIdToCheck = userId;
    
    // Only fetch user if userId not provided
    if (!userIdToCheck) {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;
      userIdToCheck = user.id;
    }

    const { data } = await supabase
      .from('admin_users')
      .select('id')
      .eq('user_id', userIdToCheck)
      .eq('is_active', true)
      .maybeSingle(); // Use maybeSingle instead of single to avoid error when no row found

    return !!data;
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
}

/**
 * Check if current user is a super admin
 */
export async function checkIsSuperAdmin() {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data, error } = await supabase
      .from('admin_users')
      .select('role')
      .eq('user_id', user.id)
      .eq('role', 'super_admin')
      .eq('is_active', true)
      .single();

    if (error || !data) return false;
    return true;
  } catch (error) {
    console.error('Error checking super admin status:', error);
    return false;
  }
}

/**
 * Get all admin users
 */
export async function getAdminUsers() {
  try {
    const { data, error } = await supabase
      .from('admin_users')
      .select(`
        id,
        user_id,
        email,
        role,
        permissions,
        is_active,
        created_at
      `)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error fetching admin users:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Add a new admin user
 */
export async function addAdminUser(email, role = 'admin') {
  try {
    // Use RPC function to add admin (this handles user lookup server-side)
    const { data, error } = await supabase.rpc('add_admin_user', {
      admin_email: email,
      admin_role: role
    });

    if (error) throw error;

    return { success: true, data };
  } catch (error) {
    console.error('Error adding admin user:', error);
    
    // Parse error message for better user feedback
    let errorMessage = error.message;
    if (errorMessage.includes('User not found')) {
      errorMessage = 'User must sign up first before being made an admin';
    } else if (errorMessage.includes('already an admin')) {
      errorMessage = 'This user is already an admin';
    } else if (errorMessage.includes('Unauthorized')) {
      errorMessage = 'Only super admins can add other admins';
    }
    
    return { success: false, error: errorMessage };
  }
}

/**
 * Remove an admin user
 */
export async function removeAdminUser(adminId) {
  try {
    // Use RPC function to delete admin user with proper permission checks
    const { data, error } = await supabase.rpc('delete_admin_user', {
      target_admin_id: adminId
    });

    if (error) throw error;
    
    if (!data.success) {
      return { success: false, error: data.error };
    }

    // Log activity
    await logAdminActivity('remove_admin', { adminId });

    return { success: true };
  } catch (error) {
    console.error('Error removing admin user:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Update admin user status
 */
export async function updateAdminStatus(adminId, isActive) {
  try {
    const { error } = await supabase
      .from('admin_users')
      .update({ is_active: isActive })
      .eq('id', adminId);

    if (error) throw error;

    // Log activity
    await logAdminActivity('update_admin_status', { adminId, isActive });

    return { success: true };
  } catch (error) {
    console.error('Error updating admin status:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Update admin user role
 */
export async function updateAdminRole(adminId, role) {
  try {
    // Use RPC function to update admin role
    const { data, error } = await supabase.rpc('update_admin_role', {
      target_admin_id: adminId,
      new_role: role
    });

    if (error) throw error;

    return { success: true, data };
  } catch (error) {
    console.error('Error updating admin role:', error);
    
    // Parse error message for better user feedback
    let errorMessage = error.message;
    if (errorMessage.includes('Unauthorized')) {
      errorMessage = 'Only super admins can change admin roles';
    } else if (errorMessage.includes('Cannot change')) {
      errorMessage = 'Cannot change the last super admin to regular admin';
    }
    
    return { success: false, error: errorMessage };
  }
}

/**
 * Get site settings
 */
export async function getSiteSettings(category = null) {
  try {
    let query = supabase.from('site_settings').select('*');
    
    if (category) {
      query = query.eq('category', category);
    }

    const { data, error } = await query;

    if (error) throw error;

    // Convert array to object for easier access
    const settings = {};
    data.forEach(item => {
      settings[item.key] = item.value;
    });

    return { success: true, data: settings };
  } catch (error) {
    console.error('Error fetching site settings:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Update site setting
 */
export async function updateSiteSetting(key, value) {
  try {
    const { error } = await supabase
      .from('site_settings')
      .update({ 
        value: JSON.stringify(value),
        updated_at: new Date().toISOString(),
        updated_by: (await supabase.auth.getUser()).data.user?.id
      })
      .eq('key', key);

    if (error) throw error;

    // Log activity
    await logAdminActivity('update_setting', { key, value });

    return { success: true };
  } catch (error) {
    console.error('Error updating site setting:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Update multiple site settings at once
 */
export async function updateSiteSettings(settings) {
  try {
    const userId = (await supabase.auth.getUser()).data.user?.id;
    const updates = [];

    for (const [key, value] of Object.entries(settings)) {
      updates.push(
        supabase
          .from('site_settings')
          .update({ 
            value: JSON.stringify(value),
            updated_at: new Date().toISOString(),
            updated_by: userId
          })
          .eq('key', key)
      );
    }

    await Promise.all(updates);

    // Log activity
    await logAdminActivity('update_settings', { settings: Object.keys(settings) });

    return { success: true };
  } catch (error) {
    console.error('Error updating site settings:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get admin activity logs
 */
export async function getAdminActivityLogs(limit = 50) {
  try {
    const { data, error } = await supabase
      .from('admin_activity_logs')
      .select(`
        id,
        action,
        details,
        created_at,
        admin_users (
          email,
          role
        )
      `)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error fetching activity logs:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Log admin activity
 */
async function logAdminActivity(action, details = {}) {
  try {
    const { error } = await supabase.rpc('log_admin_activity', {
      action_type: action,
      action_details: details
    });

    if (error) throw error;
  } catch (error) {
    console.error('Error logging admin activity:', error);
  }
}

/**
 * Get dashboard statistics
 */
export async function getDashboardStats() {
  try {
    // For now, return mock data since we can't access auth.users directly
    // In production, you would create a server-side function or edge function
    // to aggregate this data
    
    // Get admin users count
    const { data: adminUsers } = await supabase
      .from('admin_users')
      .select('id', { count: 'exact' });

    // Get activity logs count
    const { data: recentLogs } = await supabase
      .from('admin_activity_logs')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(10);

    return {
      success: true,
      data: {
        totalUsers: 127, // Mock data - replace with actual count
        activeUsers: 89,  // Mock data
        totalCards: 245,  // Mock data
        activeCards: 198, // Mock data
        kycApproved: 103, // Mock data
        kycPending: 12,   // Mock data
        kycRejected: 8,   // Mock data
        adminCount: adminUsers?.length || 0,
        recentActivity: recentLogs || []
      }
    };
  } catch (error) {
    console.error('Error fetching dashboard stats:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Initialize first admin (call this once after first user signup)
 */
export async function initializeFirstAdmin(email) {
  try {
    // Use RPC function that bypasses RLS
    const { data, error } = await supabase.rpc('initialize_first_admin', {
      admin_email: email
    });

    if (error) throw error;

    return { success: true, data };
  } catch (error) {
    console.error('Error initializing first admin:', error);
    
    // Parse error message for better user feedback
    let errorMessage = error.message;
    if (errorMessage.includes('Admin already exists')) {
      errorMessage = 'An admin has already been set up';
    } else if (errorMessage.includes('not authenticated')) {
      errorMessage = 'You must be logged in to set up admin access';
    } else if (errorMessage.includes('Email does not match')) {
      errorMessage = 'Email does not match your logged in account';
    }
    
    return { success: false, error: errorMessage };
  }
}